import { Component, Input, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { AlertController, IonicModule, ModalController } from '@ionic/angular';
import { firstValueFrom } from 'rxjs';
import { DataService } from '../../data.service';

/**
 *  PAPÍR BIZONYÍTVÁNY NYOMTATÁSA jóváhagyott, előnyomott nyomtatványra (backend/papir-bizonyitvany.js).
 *
 *  Nkt. 57. § (5): papír bizonyítvány csak a miniszter által jóváhagyott nyomtatványon adható ki;
 *  Nkt. 99/M. §: a digitális bizonyítvány felmenő rendszerben jön (2025/2026-tól az 1., a 9., illetve
 *  a 6/8 évfolyamos gimnázium belépő évfolyamán) – a többi évfolyam még papírt kap.
 *
 *  Fülek:
 *    - Nyomtatás: osztály → tanulók, nyomtatvány-sorszám tanulónként (vagy sorozat + kezdő sorszám),
 *      előnézet, nyomtatás + a sorszámok rögzítése (szigorú számadás).
 *    - Szigorú számadás: kiadott / rontott / bevont példányok, nyomtatható nyilvántartás.
 *    - Évfolyam-szabály: melyik osztály digitális, melyik papír; a képzés típusa és kézi felülírás.
 *    - Elrendezés: a mezők helye mm-ben, globális eltolás, rácsos tesztlap, próbanyomtatás.
 *    - Sablonok: a szabad formátumú (Word) sablon feltöltése – csak digitális bizonyítványhoz/díszpéldányhoz.
 */
@Component({
  selector: 'app-papir-bizonyitvany',
  standalone: true,
  imports: [CommonModule, FormsModule, IonicModule],
  templateUrl: './papir-bizonyitvany.component.html',
  styleUrls: ['./papir-bizonyitvany.component.scss'],
})
export class PapirBizonyitvanyComponent implements OnInit {
  /** Intézmény-azonosító (OM/feladatellátási hely, pl. 203508/001). */
  @Input() intezmeny = '';
  /** A tanév kezdő éve (YYYY). */
  @Input() tanevEv = '';
  @Input() osztalyok: any[] = [];
  @Input() kivosztaly: any = null;

  readonly sablonFigyelmeztetes = 'Szabad formátumú sablon csak a digitális bizonyítványhoz és a díszpéldányhoz használható; papír bizonyítvány csak jóváhagyott, előnyomott nyomtatványon adható ki.';

  ful: 'nyomtatas' | 'szamadas' | 'szabaly' | 'elrendezes' | 'sablon' = 'nyomtatas';
  fut = false;

  // ── Nyomtatás
  osztaly: any = null;
  tipus = '';
  nevsor: any = null;
  tetelek: Array<{ oktazon: string; nev: string; kijelolt: boolean; sorozat: string; sorszam: string; kiadott: any; korabbi: any; rontottak: any[]; forma: string; formaIndok: string }> = [];
  tomegSorozat = '';
  tomegKezdo = '';
  fuzetFolytatas = true;
  keltDatum = '';
  szemelyi: 'auto' | 'mind' | 'nem' = 'auto';
  oldalValasztas: Record<string, boolean> = {};
  csakElsoKeresztnev = false;
  figyelmeztetesek: string[] = [];
  hibak: Array<{ nev: string; hiba: string }> = [];

  // ── Évfolyam-szabály
  szabaly: any = null;
  ujBetujel = { betujel: '', kepzes: '', forma: 'auto' };

  // ── Elrendezés
  elrLista: any[] = [];
  elrTipus = 'AT1001';
  elr: any = null;
  elrOldal = '';
  mezoNevek: Record<string, string> = {};
  oszlopNevek: Record<string, string> = {};
  elrValtozott = false;

  // ── Szigorú számadás
  szamadasEv = '';
  szamadasAllapot = '';
  szamadasKereso = '';
  szamadas: any[] = [];
  uresRontott = { tipus: 'AT1001', sorozat: '', sorszam: '', ok: '' };

  // ── Sablonok
  sablonok: any[] = [];
  sablonKulcs = 'bizonyitvany';
  sablonFajl: File | null = null;
  sablonTudomasul = false;
  /** A bizonyítvány-sablonból hiányzó 20/2012. EMMI r. 96. §-os elemek (backend/bizonyitvany-tartalom.js) – kulcs → lista. */
  sablonHianyok: Record<string, Array<{ pont: string; nev: string; kulcsok: string[] }>> = {};

  constructor(private http: HttpClient, private data: DataService, private modal: ModalController, private alert: AlertController) {}

  private get api() { return '/api/papirbizonyitvany'; }
  private get ip() { return String(this.intezmeny || '').replace(/^\/+|\/+$/g, ''); }

  async ngOnInit() {
    this.szamadasEv = this.tanevEv;
    this.keltDatum = new Date().toISOString().slice(0, 10);
    await this.szabalyToltes();
    if (this.kivosztaly) { this.osztaly = this.kivosztaly; await this.nevsorToltes(); }
  }

  bezar() { this.modal.dismiss(); }

  async fulValt() {
    if (this.ful === 'szamadas') await this.szamadasToltes();
    if (this.ful === 'elrendezes' && !this.elr) await this.elrToltes();
    if (this.ful === 'sablon') await this.sablonToltes();
    if (this.ful === 'szabaly') await this.szabalyToltes();
  }

  /* ─────────────── közös */

  private async hibaUzenet(e: any): Promise<{ uzenet: string; hibak: any[] }> {
    let body = e?.error;
    if (body instanceof Blob) { try { body = JSON.parse(await body.text()); } catch { body = null; } }
    return { uzenet: body?.error || e?.message || 'Hiba történt.', hibak: Array.isArray(body?.hibak) ? body.hibak : [] };
  }

  /** PDF POST-tal: a lapot a kattintáskor nyitjuk (különben előugró-tiltás), a választ blobként töltjük bele. */
  private async pdfPost(url: string, body: any, fajlNev: string): Promise<string[]> {
    const lap = window.open('', '_blank');
    try {
      const v = await firstValueFrom(this.http.post(url, body, { responseType: 'blob', observe: 'response' }));
      const blob = new Blob([v.body as Blob], { type: 'application/pdf' });
      const u = URL.createObjectURL(blob);
      if (lap) lap.location.href = u;
      else { const a = document.createElement('a'); a.href = u; a.download = fajlNev; document.body.appendChild(a); a.click(); a.remove(); }
      setTimeout(() => URL.revokeObjectURL(u), 120000);
      const fej = v.headers.get('X-Papirbiz-Figyelmeztetes');
      try { return fej ? JSON.parse(decodeURIComponent(fej)) : []; } catch { return []; }
    } catch (e) {
      try { lap?.close(); } catch { /* ignore */ }
      throw e;
    }
  }

  formaNev(f: string) { return f === 'digitalis' ? 'Digitális' : f === 'papir' ? 'Papír' : f; }

  /* ─────────────── Nyomtatás */

  async nevsorToltes() {
    this.nevsor = null; this.tetelek = []; this.hibak = []; this.figyelmeztetesek = [];
    if (!this.osztaly || !this.tanevEv) return;
    this.fut = true;
    try {
      const p = new URLSearchParams({ ev: this.tanevEv, osztaly: String(this.osztaly) });
      if (this.tipus) p.set('tipus', this.tipus);
      this.nevsor = await firstValueFrom(this.http.get<any>(`${this.api}/nevsor/${this.ip}?${p}`));
      if (!this.tipus) this.tipus = this.nevsor.tipus;
      this.tetelek = (this.nevsor.tanulok || []).map((t: any) => ({
        oktazon: t.oktazon, nev: t.nev, kiadott: t.kiadott, korabbi: t.korabbi, rontottak: t.rontottak || [],
        forma: t.forma || this.nevsor?.szabaly?.forma || '', formaIndok: t.forma_indok || '',
        kijelolt: (t.forma || this.nevsor?.szabaly?.forma) !== 'digitalis',
        sorozat: t.kiadott?.sorozat || (this.fuzetFolytatas ? t.korabbi?.sorozat : '') || '',
        sorszam: t.kiadott?.sorszam || (this.fuzetFolytatas ? t.korabbi?.sorszam : '') || '',
      }));
      await this.oldalakToltes();
    } catch (e) {
      this.data.sendToast((await this.hibaUzenet(e)).uzenet, 'error');
    } finally { this.fut = false; }
  }

  async tipusValt() { await this.nevsorToltes(); }
  /** Osztályváltáskor a nyomtatványtípus az osztály szabályából jön (újra). */
  async osztalyValt() { this.tipus = ''; await this.nevsorToltes(); }

  /** A választható évfolyam-oldalak (a „egyszer” kitöltendő személyi oldal külön kapcsoló). */
  evfolyamOldalak: any[] = [];
  private async oldalakToltes() {
    try {
      const r: any = await firstValueFrom(this.http.get(`${this.api}/elrendezes/${this.ip}?tipus=${encodeURIComponent(this.tipus)}`));
      this.evfolyamOldalak = (r?.elrendezes?.oldalak || []).filter((o: any) => !o.egyszer);
      const uj: Record<string, boolean> = {};
      for (const o of this.evfolyamOldalak) uj[o.kulcs] = this.oldalValasztas[o.kulcs] ?? true;
      this.oldalValasztas = uj;
      if (!this.elrLista.length) this.elrLista = r?.lista || [];
    } catch { this.evfolyamOldalak = []; }
  }

  get kijeloltDb() { return this.tetelek.filter((t) => t.kijelolt).length; }
  /** Van-e papír bizonyítványos tanuló (szakképzőben tanulónként dől el – Szkt. 128/C. §). */
  get papirOsztaly(): boolean {
    if (this.nevsor?.szabaly?.forma === 'papir') return true;
    return !!this.nevsor?.szabaly?.szakkepzo && (this.nevsor?.tanulok || []).some((t: any) => t?.forma === 'papir');
  }
  osszesKijelol(v: boolean) { for (const t of this.tetelek) t.kijelolt = v && t.forma !== 'digitalis'; }

  /** Sorozat + kezdő sorszám: a kijelölt, még szám nélküli tanulók egymás utáni sorszámot kapnak. */
  tomegesKitoltes() {
    const kezdo = String(this.tomegKezdo || '').trim();
    if (!/^\d{1,15}$/.test(kezdo)) { this.data.sendToast('A kezdő sorszám csak számjegyekből állhat (legfeljebb 15).', 'warning'); return; }
    const hossz = kezdo.length;
    let n = Number(kezdo);
    let db = 0;
    for (const t of this.tetelek) {
      if (!t.kijelolt || t.kiadott || String(t.sorszam || '').trim()) continue;
      t.sorozat = String(this.tomegSorozat || '').trim().toUpperCase();
      t.sorszam = n.toString().padStart(hossz, '0');
      n += 1; db++;
    }
    this.data.sendToast(db ? `${db} tanuló kapott sorszámot.` : 'Nincs szám nélküli kijelölt tanuló.', db ? 'success' : 'warning');
  }

  sorszamTorol() { for (const t of this.tetelek) if (t.kijelolt && !t.kiadott) { t.sorszam = ''; t.sorozat = ''; } }

  private nyomtatasTorzs(elonezet: boolean) {
    const oldalak = Object.entries(this.oldalValasztas).filter(([, v]) => v).map(([k]) => k);
    return {
      osztaly: this.osztaly, ev: this.tanevEv, tipus: this.tipus, elonezet, datum: this.keltDatum,
      szemelyi: this.szemelyi, oldalak, csakElsoKeresztnev: this.csakElsoKeresztnev,
      tetelek: this.tetelek.filter((t) => t.kijelolt).map((t) => ({ oktazon: t.oktazon, sorozat: t.sorozat, sorszam: t.sorszam })),
    };
  }

  async nyomtat(elonezet: boolean) {
    this.hibak = []; this.figyelmeztetesek = [];
    if (!this.kijeloltDb) { this.data.sendToast('Jelölj ki legalább egy tanulót.', 'warning'); return; }
    if (!elonezet) {
      const hianyzik = this.tetelek.filter((t) => t.kijelolt && !String(t.sorszam || '').trim());
      if (hianyzik.length) { this.data.sendToast(`Hiányzó sorszám: ${hianyzik.map((t) => t.nev).join(', ')}`, 'error'); return; }
      const a = await this.alert.create({
        header: 'Nyomtatás és rögzítés',
        message: `${this.kijeloltDb} tanuló nyomtatványának sorszáma a szigorú számadásba kerül (kiadott). Nyomtatáskor a nyomtatóban „tényleges méret” legyen (ne „oldalhoz igazítás”). Ha egy példány elromlik, jelöld rontottnak, és új sorszámmal nyomtasd újra.`,
        buttons: [{ text: 'Mégse', role: 'cancel' }, { text: 'Nyomtatás', role: 'ok' }],
      });
      await a.present();
      if ((await a.onDidDismiss()).role !== 'ok') return;
    }
    this.fut = true;
    try {
      const osztalyNev = this.nevsor?.osztaly?.nev || 'osztaly';
      this.figyelmeztetesek = await this.pdfPost(`${this.api}/nyomtatas/${this.ip}`, this.nyomtatasTorzs(elonezet),
        `papir-bizonyitvany-${osztalyNev}${elonezet ? '-elonezet' : ''}.pdf`);
      if (!elonezet) {
        this.data.sendToast('A sorszámok rögzítve, a nyomtatandó PDF megnyílt.', 'success');
        await this.nevsorToltes();
      }
    } catch (e) {
      const h = await this.hibaUzenet(e);
      this.hibak = h.hibak;
      this.data.sendToast(h.uzenet, 'error');
    } finally { this.fut = false; }
  }

  /** Egy kiadott példány rontottá / bevonttá nyilvánítása (ok kötelező). */
  async allapotValt(id: string, mit: 'rontott' | 'bevon', cim: string) {
    const a = await this.alert.create({
      header: mit === 'rontott' ? 'Rontott példány' : 'Bevonás',
      message: mit === 'rontott'
        ? `${cim} – a sorszám többé nem használható. A tanuló új sorszámmal újranyomtatható.`
        : `${cim} – a kiadott bizonyítvány bevonása (pl. elveszett és pótolják, visszavonták). A sorszám többé nem használható.`,
      inputs: [{ name: 'ok', type: 'text', placeholder: mit === 'rontott' ? 'Ok (pl. elírás, papírelakadás)' : 'Ok' }],
      buttons: [{ text: 'Mégse', role: 'cancel' }, { text: mit === 'rontott' ? 'Rontottnak jelölés' : 'Bevonás', role: 'ok' }],
    });
    await a.present();
    const { role, data } = await a.onDidDismiss();
    if (role !== 'ok') return;
    const ok = String(data?.values?.ok || '').trim();
    if (!ok) { this.data.sendToast('Az ok megadása kötelező.', 'warning'); return; }
    try {
      await firstValueFrom(this.http.post(`${this.api}/${mit === 'rontott' ? 'rontott' : 'bevon'}/${this.ip}`, { id, ok }));
      this.data.sendToast(mit === 'rontott' ? 'Rontottnak jelölve.' : 'Bevonva.', 'success');
      if (this.ful === 'szamadas') await this.szamadasToltes(); else await this.nevsorToltes();
    } catch (e) { this.data.sendToast((await this.hibaUzenet(e)).uzenet, 'error'); }
  }

  /* ─────────────── Szigorú számadás */

  async szamadasToltes() {
    const p = new URLSearchParams();
    if (this.szamadasEv) p.set('ev', this.szamadasEv);
    if (this.szamadasAllapot) p.set('allapot', this.szamadasAllapot);
    if (this.szamadasKereso.trim()) p.set('q', this.szamadasKereso.trim());
    try {
      const r: any = await firstValueFrom(this.http.get(`${this.api}/nyilvantartas/${this.ip}?${p}`));
      this.szamadas = r?.sorok || [];
      if (!this.elrLista.length) {
        const l: any = await firstValueFrom(this.http.get(`${this.api}/elrendezes/${this.ip}`));
        this.elrLista = l?.lista || [];
      }
    } catch (e) { this.data.sendToast((await this.hibaUzenet(e)).uzenet, 'error'); }
  }

  szamadasPdf() {
    const p = new URLSearchParams();
    if (this.szamadasEv) p.set('ev', this.szamadasEv);
    if (this.szamadasAllapot) p.set('allapot', this.szamadasAllapot);
    this.data.megnyitFajl(`${this.api}/nyilvantartas-pdf/${this.ip}?${p}`, 'Szigorú számadás – bizonyítvány');
  }

  async uresRontottRogzit() {
    const u = this.uresRontott;
    if (!String(u.sorszam).trim() || !String(u.ok).trim()) { this.data.sendToast('Add meg a sorszámot és a rontás okát.', 'warning'); return; }
    try {
      await firstValueFrom(this.http.post(`${this.api}/rontott/${this.ip}`, { ...u, ev: this.szamadasEv || this.tanevEv }));
      this.data.sendToast('A rontott nyomtatvány rögzítve.', 'success');
      this.uresRontott = { ...u, sorszam: '', ok: '' };
      await this.szamadasToltes();
    } catch (e) { this.data.sendToast((await this.hibaUzenet(e)).uzenet, 'error'); }
  }

  async megjegyzes(s: any) {
    const a = await this.alert.create({
      header: 'Megjegyzés', message: `${s.sorozat} ${s.sorszam} – ${s.tanulo_nev || ''}`,
      inputs: [{ name: 'm', type: 'textarea', value: s.megjegyzes || '' }],
      buttons: [{ text: 'Mégse', role: 'cancel' }, { text: 'Mentés', role: 'ok' }],
    });
    await a.present();
    const { role, data } = await a.onDidDismiss();
    if (role !== 'ok') return;
    try {
      await firstValueFrom(this.http.post(`${this.api}/megjegyzes/${this.ip}`, { id: s.id, megjegyzes: data?.values?.m || '' }));
      await this.szamadasToltes();
    } catch (e) { this.data.sendToast((await this.hibaUzenet(e)).uzenet, 'error'); }
  }

  tipusKod(t: string) { return this.elrLista.find((x) => x.tipus === t)?.kod || t; }

  /* ─────────────── Évfolyam-szabály */

  async szabalyToltes() {
    if (!this.tanevEv || !this.ip) return;
    try {
      this.szabaly = await firstValueFrom(this.http.get<any>(`${this.api}/szabaly/${this.ip}?ev=${this.tanevEv}`));
      this.elrLista = this.szabaly?.nyomtatvanyok || this.elrLista;
    } catch (e) { this.data.sendToast((await this.hibaUzenet(e)).uzenet, 'error'); }
  }

  /** A forma a nyomtatás-fülön: az osztály-választóban jelezzük, melyik kap papírt. */
  osztalyForma(id: any) { return this.szabaly?.osztalyok?.find((o: any) => String(o.osztaly) === String(id)); }

  async szabalyMent(kulcs: string, kepzes: string, forma: string, nyomtatvanyTipus = '') {
    try {
      await firstValueFrom(this.http.put(`${this.api}/szabaly/${this.ip}`, { kulcs, kepzes, forma, nyomtatvany_tipus: nyomtatvanyTipus }));
      this.data.sendToast('Mentve.', 'success');
      await this.szabalyToltes();
      if (this.osztaly) await this.nevsorToltes();
    } catch (e) { this.data.sendToast((await this.hibaUzenet(e)).uzenet, 'error'); }
  }

  async betujelMent() {
    const b = this.ujBetujel;
    if (!String(b.betujel).trim()) { this.data.sendToast('Add meg a betűjelet (pl. E).', 'warning'); return; }
    await this.szabalyMent(`betu:${String(b.betujel).trim()}`, b.kepzes, b.forma);
    this.ujBetujel = { betujel: '', kepzes: '', forma: 'auto' };
  }

  async betujelTorol(b: any) {
    try {
      await firstValueFrom(this.http.put(`${this.api}/szabaly/${this.ip}`, { kulcs: `betu:${b.betujel}`, torles: true }));
      await this.szabalyToltes();
    } catch (e) { this.data.sendToast((await this.hibaUzenet(e)).uzenet, 'error'); }
  }

  forrasNev(f: string) {
    return ({ osztaly: 'osztályra beállítva', betujel: 'betűjel szerint', intezmeny: 'intézményi alap', iskolatipus: 'az intézménytípus szerint', becsult: 'becsült – ellenőrizd!' } as any)[f] || f;
  }

  /* ─────────────── Elrendezés */

  async elrToltes(tipus = this.elrTipus) {
    try {
      const r: any = await firstValueFrom(this.http.get(`${this.api}/elrendezes/${this.ip}?tipus=${encodeURIComponent(tipus)}`));
      this.elrLista = r?.lista || [];
      this.mezoNevek = r?.mezok || {};
      this.oszlopNevek = r?.oszlopMezok || {};
      this.elr = r?.elrendezes || null;
      this.elrTipus = this.elr?.tipus || tipus;
      this.elrOldal = this.elr?.oldalak?.[0]?.kulcs || '';
      this.elrValtozott = false;
    } catch (e) { this.data.sendToast((await this.hibaUzenet(e)).uzenet, 'error'); }
  }

  get aktOldal() { return this.elr?.oldalak?.find((o: any) => o.kulcs === this.elrOldal); }
  get mezoKulcsok() { return Object.keys(this.mezoNevek); }
  get oszlopKulcsok() { return Object.keys(this.oszlopNevek); }
  valtozott() { this.elrValtozott = true; }
  kiValt(m: any, be: boolean) { if (be) delete m.ki; else m.ki = false; this.valtozott(); }
  get tanevCim() { const e = Number(this.szabaly?.tanev); return e ? `${e}/${e + 1}` : ''; }

  ujMezo() {
    const o = this.aktOldal; if (!o) return;
    o.mezok.push({ kulcs: 'nev', x: 10, y: 20, w: 60 });
    this.valtozott();
  }
  mezoTorol(i: number) { this.aktOldal?.mezok.splice(i, 1); this.valtozott(); }
  sorokValt(m: any) {
    if (Number(m.sorok) > 1 && !m.sorkoz) m.sorkoz = 5;
    this.valtozott();
  }

  async elrMent() {
    try {
      await firstValueFrom(this.http.put(`${this.api}/elrendezes/${this.ip}`, { tipus: this.elrTipus, elrendezes: this.elr }));
      this.data.sendToast('Az elrendezés mentve.', 'success');
      await this.elrToltes(this.elrTipus);
    } catch (e) { this.data.sendToast((await this.hibaUzenet(e)).uzenet, 'error'); }
  }

  async elrVisszaallit() {
    const beepitett = this.elrLista.find((x) => x.tipus === this.elrTipus)?.beepitett;
    const a = await this.alert.create({
      header: beepitett ? 'Visszaállítás' : 'Nyomtatványtípus törlése',
      message: beepitett ? 'A bemért elrendezés törlődik, a beépített (becsült) alap marad. Folytatod?' : 'Az egyedi nyomtatványtípus elrendezése törlődik. Folytatod?',
      buttons: [{ text: 'Mégse', role: 'cancel' }, { text: 'Igen', role: 'ok' }],
    });
    await a.present();
    if ((await a.onDidDismiss()).role !== 'ok') return;
    try {
      await firstValueFrom(this.http.delete(`${this.api}/elrendezes/${this.ip}?tipus=${encodeURIComponent(this.elrTipus)}`));
      await this.elrToltes(beepitett ? this.elrTipus : 'AT1001');
    } catch (e) { this.data.sendToast((await this.hibaUzenet(e)).uzenet, 'error'); }
  }

  /** Új nyomtatványtípus (pl. A.T. 1004) a jelenlegi elrendezés másolataként. */
  async ujTipus() {
    const a = await this.alert.create({
      header: 'Új nyomtatványtípus',
      message: 'A jelenlegi elrendezés másolata lesz; utána a mezőket a valódi nyomtatványon bemérve igazítsd.',
      inputs: [
        { name: 'tipus', type: 'text', placeholder: 'Azonosító (pl. AT1004)' },
        { name: 'kod', type: 'text', placeholder: 'Nyomtatványkód (pl. A.T. 1004)' },
        { name: 'nev', type: 'text', placeholder: 'Megnevezés' },
      ],
      buttons: [{ text: 'Mégse', role: 'cancel' }, { text: 'Létrehozás', role: 'ok' }],
    });
    await a.present();
    const { role, data } = await a.onDidDismiss();
    if (role !== 'ok') return;
    const tipus = String(data?.values?.tipus || '').replace(/[^A-Za-z0-9_-]/g, '');
    if (!tipus) { this.data.sendToast('Add meg az azonosítót (betű, szám).', 'warning'); return; }
    if (this.elrLista.some((x) => x.tipus === tipus)) { this.data.sendToast('Ilyen azonosító már van.', 'warning'); return; }
    const uj = JSON.parse(JSON.stringify(this.elr));
    uj.kod = String(data?.values?.kod || tipus); uj.nev = String(data?.values?.nev || uj.kod); uj.alap = this.elrTipus;
    try {
      await firstValueFrom(this.http.put(`${this.api}/elrendezes/${this.ip}`, { tipus, elrendezes: uj }));
      await this.elrToltes(tipus);
    } catch (e) { this.data.sendToast((await this.hibaUzenet(e)).uzenet, 'error'); }
  }

  /** Rácsos tesztlap / próbanyomtatás a JELENLEGI (akár még nem mentett) elrendezéssel. */
  async elrPdf(mit: 'tesztlap' | 'proba', keret = true) {
    try {
      const oldal = mit === 'tesztlap' ? this.elrOldal : '';
      const fig = await this.pdfPost(`${this.api}/${mit}/${this.ip}`, { tipus: this.elrTipus, elrendezes: this.elr, oldal, keret: keret ? '1' : '0' },
        `${mit}-${this.elrTipus}.pdf`);
      if (fig.length) this.data.sendToast(fig[0], 'warning');
    } catch (e) { this.data.sendToast((await this.hibaUzenet(e)).uzenet, 'error'); }
  }

  /* ─────────────── Sablonok */

  async sablonToltes() {
    try {
      const r: any = await firstValueFrom(this.http.get(`${this.api}/sablon/${this.ip}`));
      this.sablonok = r?.sablonok || [];
    } catch (e) { this.data.sendToast((await this.hibaUzenet(e)).uzenet, 'error'); }
    //  96. §-ellenőrzés: csak jelzés (a régi sablonnal a generálás megy tovább); a hibája nem akadály.
    try {
      const e: any = await firstValueFrom(this.http.get(`/api/bizonyitvany-tartalom/sablon-ellenorzes/${this.ip}`));
      this.sablonHianyok = {};
      for (const s of e?.sablonok || []) if (s?.megvan && s?.olvashato) this.sablonHianyok[s.kulcs] = s.hianyok || [];
    } catch (_) { this.sablonHianyok = {}; }
  }

  /** A beépített, a 96. § minden elemét tartalmazó Word-alapsablon (kiindulásnak a saját sablonhoz). */
  alapsablonLetoltes() { this.data.megnyitFajl('/api/bizonyitvany-tartalom/alapsablon', 'bizonyitvany-96.docx'); }

  /** „az iskola címe ((2)), …” – a sablon-listában. */
  hianyFelsorolas(kulcs: string): string {
    return (this.sablonHianyok[kulcs] || []).map((h) => `${h.nev} (${h.pont})`).join(', ');
  }

  sablonValaszt(ev: any) { this.sablonFajl = ev?.target?.files?.[0] || null; }

  async sablonFeltolt() {
    if (!this.sablonFajl) { this.data.sendToast('Válassz egy .docx fájlt.', 'warning'); return; }
    if (!this.sablonTudomasul) { this.data.sendToast(this.sablonFigyelmeztetes, 'warning'); return; }
    const fd = new FormData();
    fd.append('sablon', this.sablonKulcs);
    fd.append('tudomasul', '1');
    fd.append('fajl', this.sablonFajl, this.sablonFajl.name);
    this.fut = true;
    try {
      await firstValueFrom(this.http.post(`${this.api}/sablon/${this.ip}`, fd));
      this.data.sendToast('A sablon feltöltve.', 'success');
      this.sablonFajl = null; this.sablonTudomasul = false;
      await this.sablonToltes();
    } catch (e) { this.data.sendToast((await this.hibaUzenet(e)).uzenet, 'error'); }
    finally { this.fut = false; }
  }
}
